import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { Film, Youtube, ExternalLink, Target, Eye, Heart, BookOpen, Sparkles } from 'lucide-react';

const ValueCard = ({ icon: Icon, title, description }) => (
  <div className="p-6 bg-[#121917] border border-[#52ea88]/10 hover:border-[#52ea88]/30 transition-all duration-300">
    <div className="w-12 h-12 border border-[#52ea88]/30 flex items-center justify-center mb-4">
      <Icon className="w-5 h-5 text-[#52ea88]" />
    </div>
    <h3 className="text-lg font-semibold text-[#E6FFF6] mb-2">{title}</h3>
    <p className="text-[#9FBFB4] text-sm leading-relaxed">{description}</p>
  </div>
);

const AboutPage = () => {
  const { t } = useLanguage();

  const values = [
    {
      icon: BookOpen,
      title: t('about.values.deepAnalysis'),
      description: t('about.values.deepAnalysisDesc'),
    },
    {
      icon: Eye,
      title: t('about.values.visualLanguage'),
      description: t('about.values.visualLanguageDesc'),
    },
    {
      icon: Heart,
      title: t('about.values.emotionalImpact'),
      description: t('about.values.emotionalImpactDesc'),
    },
    {
      icon: Sparkles,
      title: t('about.values.culturalContext'),
      description: t('about.values.culturalContextDesc'),
    },
  ];

  return (
    <div className="bg-[#0B0F0E] min-h-screen">
      <Header />

      <main className="pt-32 pb-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
          {/* Hero Section */}
          <div className="max-w-3xl mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#52ea88]/20 bg-[#1a4a2e]/30 mb-6">
              <Target className="w-4 h-4 text-[#52ea88]" />
              <span className="text-[#52ea88] text-sm font-medium tracking-wider uppercase">
                {t('about.ourMission')}
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#E6FFF6] leading-tight mb-6">
              {t('about.title')}
            </h1>
            
            <p className="text-xl text-[#9FBFB4] leading-relaxed mb-8">
              {t('about.tagline').split('.')[0]}. <span className="text-[#52ea88] font-medium">{t('about.tagline').split('.')[1]?.trim() || ''}</span>
            </p>
            
            <div className="w-20 h-1 bg-[#52ea88]"></div>
          </div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-3 gap-12 mb-20">
            <div className="lg:col-span-2 space-y-6">
              <p className="text-lg text-[#E6FFF6]/90 leading-relaxed">
                {t('about.intro1')}
              </p>
              
              <p className="text-lg text-[#9FBFB4] leading-relaxed">
                {t('about.intro2')}
              </p>
              
              <p className="text-lg text-[#9FBFB4] leading-relaxed">
                {t('about.intro3')}
              </p>
              
              <p className="text-lg text-[#9FBFB4] leading-relaxed">
                {t('about.intro4')}
              </p>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="p-6 bg-[#121917] border border-[#52ea88]/10">
                <h3 className="text-lg font-semibold text-[#E6FFF6] mb-4">{t('about.universeTitle')}</h3>
                <div className="space-y-4">
                  <a
                    href="https://moviematrixtrailers.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-[#0B0F0E] hover:bg-[#52ea88]/5 border border-[#52ea88]/10 hover:border-[#52ea88]/30 transition-all group"
                  >
                    <Film className="w-5 h-5 text-[#52ea88]" />
                    <div className="flex-1">
                      <span className="text-[#E6FFF6] group-hover:text-[#52ea88] transition-colors text-sm font-medium">Movie Matrix Trailers</span>
                      <p className="text-[#9FBFB4] text-xs">{t('about.trailersDesc')}</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-[#9FBFB4]" />
                  </a>
                  
                  <a
                    href="https://www.youtube.com/@TheMovieMatrixTV"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-[#0B0F0E] hover:bg-[#52ea88]/5 border border-[#52ea88]/10 hover:border-[#52ea88]/30 transition-all group"
                  >
                    <Youtube className="w-5 h-5 text-[#52ea88]" />
                    <div className="flex-1">
                      <span className="text-[#E6FFF6] group-hover:text-[#52ea88] transition-colors text-sm font-medium">Movie Matrix TV</span>
                      <p className="text-[#9FBFB4] text-xs">{t('about.tvDesc')}</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-[#9FBFB4]" />
                  </a>
                </div>
              </div>

              <div className="p-6 bg-[#52ea88]/5 border border-[#52ea88]/20">
                <h3 className="text-lg font-semibold text-[#52ea88] mb-2">{t('about.communityTitle')}</h3>
                <p className="text-[#9FBFB4] text-sm mb-4">
                  {t('about.communityDesc')}
                </p>
                <span className="text-xs text-[#52ea88]/70 uppercase tracking-wider">{t('about.comingSoon')}</span>
              </div>
            </div>
          </div>

          {/* Values Grid */}
          <div className="mb-20">
            <h2 className="text-2xl md:text-3xl font-semibold text-[#E6FFF6] mb-8">
              {t('about.valuesTitle')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <ValueCard key={index} {...value} />
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center py-12 border-t border-[#52ea88]/10">
            <h2 className="text-2xl font-semibold text-[#E6FFF6] mb-4">
              {t('about.ctaTitle')}
            </h2>
            <p className="text-[#9FBFB4] mb-8 max-w-lg mx-auto">
              {t('about.ctaDesc')}
            </p>
            <Link
              to="/reviews"
              className="inline-flex items-center gap-2 px-6 py-4 bg-[#52ea88] text-[#0B0F0E] font-medium text-lg hover:bg-[#7aff9e] transition-all duration-300"
            >
              {t('about.ctaButton')}
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
